<template>
    <div class="flex flex-column mb-3">
        <b-alert v-if="success" variant="success" show>
            <font-awesome-icon :icon="['far', 'square-check']" size="xl" class="mr-2"></font-awesome-icon>
            {{ success }}
        </b-alert>
        <b-alert v-if="error" variant="error" show>
            <font-awesome-icon :icon="['far', 'square-exclamation']" size="xl" class="mr-2"></font-awesome-icon>
            {{ error }}
        </b-alert>
        <p>{{ $t('admin.admin_labels') }}</p>
        <div v-if="loadingSigns" class="text-center mt-5 pt-4">
            <b-spinner class="text-highlight"></b-spinner>
        </div>
        <div v-if="labels.length === 0" class="text-center mt-5">
            <p>{{ $t('admin.no_label_available') }}</p>
        </div>

        <div v-else class="d-flex flex-column flex-wrap justify-content-center align-items-start mt-3 gap-3">
            <Button
                class="mt-3 mb-3"
                @click="openLabelModal()"
                :icon="['fal', 'plus']"
                :label="$t('admin.create_labels')"
            />
            <ul class="w-100">
                <AdminItem
                    v-for="label in labels"
                    :key="label.id"
                    :icon="label.icon"
                    :id="label.id"
                    :name="label.name"
                    :signBankIds="label.signBankIds"
                    :signBankId="label.signBankId"
                    :info="label.info"
                    @edit-item="openLabelModalWithName(label.id)"
                    @delete-item="deleteConfirmation(label.id)"
                />
            </ul>
        </div>
        <LabelModal :label="selectedLabel" @submit="onCompletion" />
    </div>
</template>
<script lang="ts">
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import SvgIcon from '@/components/SvgIcon.vue';
import Button from '@/components/general/Button.vue';
import { ILabel } from '../../../../functions/src/shared/contracts/api/MasterdataContract';
import { Constants } from '@/shared/Constants';
import { axiosHttp } from '@/util/AxiosHttp';
import LabelModal from './LabelModal.vue';
import FaLightIcon from '@/components/general/FaLightIcon.vue';
import AdminItem from './AdminItem.vue';

const cacheModule = namespace('cache');

@Component({ components: { SvgIcon, Button, LabelModal, FaLightIcon, AdminItem } })
export default class LabelsTab extends Vue {
    @cacheModule.Getter('labels')
    private labels: ILabel[];

    public selectedLabel: ILabel | null = null;
    public error: string | null = null;
    public success: string | null = null;
    public loadingSigns: boolean = false;

    public openLabelModal(): void {
        this.selectedLabel = null;
        this.$bvModal.show('labelModal');
    }

    public openLabelModalWithName(labelName: string): void {
        this.selectedLabel = this.labels.find((label) => label.id === labelName) || null;
        this.$bvModal.show('labelModal');
    }

    public deleteConfirmation(labelName: string): void {
        this.selectedLabel = this.labels.find((label) => label.id === labelName) || null;
        const selectedLabelId = this.selectedLabel.id;
        this.$bvModal
            .msgBoxConfirm(
                this.$t('admin.delete_label_confirmation', { selectedLabelId: selectedLabelId }).toString(),
                {
                    title: this.$t('admin.delete_label_title').toString(),
                    size: 'md',
                    buttonSize: 'md',
                    centered: true,
                    okVariant: 'danger',
                    okTitle: this.$t('admin.delete').toString(),
                    cancelVariant: 'secondary',
                    cancelTitle: this.$t('general.cancel').toString(),
                },
            )
            .then((confirmed) => {
                if (confirmed) {
                    this.deleteLabel(selectedLabelId);
                }
            });
    }

    private async deleteLabel(labelName: string): Promise<void> {
        try {
            this.error = '';
            await axiosHttp.http.delete<ILabel>(`${Constants.baseOperationsUrl}/labels/${labelName}`);
            this.success = this.$t('admin.label_deleted').toString();
            await this.onCompletion();
        } catch (error) {
            this.success = '';
            this.error = `${this.$t('admin.label_deletion_error')}: ${error}`;
        }
    }

    @cacheModule.Action
    private refreshLabels!: () => Promise<void>;

    private async handleRefresh() {
        this.loadingSigns = true;
        try {
            await this.refreshLabels();
        } catch (error) {
            this.success = '';
            this.error = `${this.$t('admin.labels_loading_error')}`;
        } finally {
            this.loadingSigns = false;
        }
    }
    public onCompletion() {
        this.success = this.selectedLabel
            ? this.$t('admin.labels_updated').toString()
            : this.$t('admin.labels_created').toString();
        this.handleRefresh;
    }
}
</script>
